import { ReactNode, useState } from "react";
import { Dot } from "../utils/PageDots";
import { getEmbedUrl } from "../utils/helpers";
import { SKPCFPartners } from "../utils/partners";
import ProgramDetailStats from "./ProgramDetailStats";
import ProgramImageGallery from "./ProgramImageGallery";

interface ProgramDetailCardProps {
    program: SKPCFPartners;
    programs: SKPCFPartners[];
    selectedIndex: number | null;
    onSelectProgram: (index: number) => void;
}

export default function ProgramDetailCard({ program, programs, selectedIndex, onSelectProgram }: ProgramDetailCardProps) {
    const color = program?.color ?? "#C2410C";
    const title = program?.title ?? "Key Tech Labs";
    const subtitle = program?.subtitle ?? "SharkGarden, a community garden in Burien.";
    const description =
        program?.description ??
        "SharkGarden produces its own energy and captures its own stormwater, which is used to nourish plants and grow food for the community. Alongside the technical skills gained, students gain social-emotional skills through Restorative Justice Circles where they learn to navigate challenges they may face in green job work environments. Key Tech Labs brings green technology to communities who otherwise would not have the opportunity.";
    const bannerImg = program?.banner ?? '';
    const badgeImg = program?.src ?? '';
    const websiteUrl = program?.website ?? '';
    const videoUrl = program?.video ?? '';
    const images = program?.images ?? [];

    const [showVideo, setShowVideo] = useState(false);
    const [showImpact, setShowImpact] = useState(false);
    const [showImages, setShowImages] = useState(false);

    const toggleVideo = () => {
        setShowVideo((prev) => {
        const next = !prev;
        if (next) {
            setShowImpact(false);
            setShowImages(false);
        }
        return next;
        });
    };

    const toggleImages = () => {
        if (!images.length) return;

        setShowImages((prev) => {
        const next = !prev;
        if (next) {
            setShowVideo(false);
            setShowImpact(false);
        }
        return next;
        });
    };

    const toggleImpact = () => {
        setShowImpact((prev) => {
        const next = !prev;
        if (next) {
            setShowVideo(false);
            setShowImages(false);
        }
        return next;
        });
    };

    return (
        <section className="relative bg-white shadow-xl">
            {/* Left accent line */}
            <div
                className="absolute left-[4.2rem] top-28 rounded-full h-5/6"
                style={{ width: 6, backgroundColor: color }}
                aria-hidden="true"
            />

            <div className="relative px-10 py-8">
                {/* Header row */}
                <div className="flex items-start gap-6">
                    {/* Badge image */}
                    <div className="shrink-0">
                        <div
                        className="h-16 w-16 rounded-full overflow-hidden bg-white"
                        style={{ boxShadow: `0 0 0 4px ${color}` }}
                        >
                        {badgeImg ? (
                            <img src={badgeImg} alt="" className="h-full w-full object-cover p-1" />
                        ) : null}
                        </div>
                    </div>

                    {/* Title / text */}
                    <div className="min-w-0">
                        <h3 className="text-4xl md:text-5xl font-extrabold leading-tight ml-8" style={{ color }}>
                        {title}
                        </h3>

                        <div className="relative overflow-hidden">
                            <img className="h-56 w-full object-cover" src={bannerImg} alt="partner banner"/>
                            <p className="flex justify-start absolute bottom-0 left-0 w-full pl-8 py-4 text-sm md:text-lg font-semibold text-gray-900 bg-white/70 backdrop-blur-sm">
                            {subtitle}
                            </p>
                        </div>
                        {description.map((paragraph, i) => (
                            <p key={i} className="mt-4 max-w-3xl text-sm md:text-lg text-gray-700 leading-relaxed">
                                {paragraph}
                            </p>
                        ))}
                    </div>
                </div>

                {/* Icon buttons */}
                <div className="mt-8 flex items-start gap-2 pl-[5.5rem] xs:gap-4">
                    <SquareIconButton color={color} ariaLabel="Play video" hoverText="Play video" onClick={toggleVideo}>
                        {/* play */}
                        <svg className="h-9 w-9" viewBox="0 0 51 59" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0.000110172 2.01076C-0.00367706 0.469412 1.66083 -0.49882 2.99514 0.268567L49.7691 27.1692C51.1025 27.936 51.1061 29.8613 49.7756 30.6352L3.13439 57.7662C1.80386 58.5401 0.136656 57.5826 0.132871 56.0423L0.000110172 2.01076Z" fill="currentColor"/>
                        </svg>
                    </SquareIconButton>

                    <SquareIconButton color={color} ariaLabel="Show images" hoverText="Show images" onClick={toggleImages} disabled={!images.length}>
                        <svg fill="#000000" viewBox="-120 -250 762 962" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M80,132V460a20,20,0,0,0,20,20H492a20,20,0,0,0,20-20V132a20,20,0,0,0-20-20H100A20,20,0,0,0,80,132Zm293.14,41.33a46,46,0,1,1-46.28,46A46.19,46.19,0,0,1,373.14,173.33Zm-261.41,276V353.85l122.76-110.2L328.27,337l-113,112.33Zm368.27,0H259l144.58-144L480,370.59Z"/><path fill="currentColor" d="M20,32A20,20,0,0,0,0,52V396a20,20,0,0,0,20,20H48V100A20,20,0,0,1,68,80H448V52a20,20,0,0,0-20-20Z"/></svg>
                    </SquareIconButton>

                    <SquareIconButton color={color} ariaLabel="Visit website" hoverText="Visit website" url={websiteUrl}>
                        {/* globe */}
                        <svg version="1.1" id="_x32_" viewBox="-125 -120 752 752">
                            <path fill="currentColor" d="M255.994,0.006C114.607,0.013,0.012,114.612,0,256c0.012,141.387,114.607,255.986,255.994,255.994
                                C397.393,511.986,511.992,397.387,512,256C511.992,114.612,397.393,0.013,255.994,0.006z M97.607,97.612
                                c23.34-23.328,51.761-41.475,83.455-52.725c-15.183,18.375-27.84,41.906-37.757,69.116H82.772
                                C87.452,108.308,92.396,102.824,97.607,97.612z M65.612,138.003h69.986c-9.008,31.929-14.41,67.834-15.363,105.997H32.327
                                C34.374,205.196,46.3,169.088,65.612,138.003z M65.612,373.997C46.3,342.912,34.374,306.804,32.327,268h87.991
                                c0.961,38.124,6.21,74.092,15.206,105.998H65.612z M97.607,414.386c-5.211-5.211-10.156-10.695-14.836-16.39h60.573
                                c4.28,11.774,9.019,22.944,14.312,33.21c6.954,13.438,14.758,25.468,23.348,35.89C149.332,455.846,120.931,437.699,97.607,414.386z
                                M243.998,479.667c-3.746-0.196-7.469-0.477-11.164-0.86c-5.89-2.64-11.722-6.25-17.5-10.961
                                c-17.632-14.359-33.976-38.671-46.398-69.85h75.061V479.667z M243.998,373.997h-83.436c-9.477-31.171-15.316-67.311-16.328-105.998
                                h99.763V373.997z M243.998,244H144.31c1.008-38.71,6.875-74.819,16.359-105.997h83.33V244z M243.998,114.003h-74.951
                                c3.109-7.79,6.367-15.312,9.934-22.195c10.64-20.625,23.17-36.89,36.354-47.656c5.777-4.71,11.609-8.32,17.5-10.96
                                c3.695-0.382,7.417-0.664,11.164-0.859V114.003z M446.392,138.003c19.312,31.085,31.234,67.194,33.281,105.997h-87.991
                                c-0.961-38.124-6.21-74.092-15.21-105.997H446.392z M414.393,97.612c5.211,5.211,10.156,10.696,14.836,16.391h-60.577
                                c-4.281-11.773-9.023-22.945-14.312-33.21c-6.953-13.437-14.758-25.468-23.347-35.89C362.668,56.16,391.065,74.301,414.393,97.612z
                                M267.998,32.333c3.746,0.195,7.469,0.484,11.16,0.859c5.89,2.649,11.723,6.25,17.504,10.96
                                c17.636,14.359,33.976,38.671,46.397,69.85h-75.061V32.333z M267.998,138.003h83.436c9.476,31.171,15.32,67.31,16.328,105.997
                                h-99.764V138.003z M267.998,268h99.685c-1.007,38.71-6.874,74.818-16.359,105.998h-83.326V268z M296.661,467.846
                                c-5.781,4.711-11.614,8.313-17.504,10.961c-3.691,0.375-7.414,0.664-11.16,0.86v-81.67h74.951
                                c-3.109,7.789-6.367,15.312-9.933,22.195C322.376,440.816,309.845,457.081,296.661,467.846z M414.393,414.386
                                c-23.336,23.328-51.764,41.476-83.459,52.725c15.187-18.375,27.835-41.905,37.757-69.115h60.538
                                C424.548,403.692,419.604,409.176,414.393,414.386z M446.392,373.997h-69.998c9.008-31.929,14.414-67.842,15.367-105.998h87.912
                                C477.626,306.804,465.704,342.912,446.392,373.997z"/>
                        </svg>
                    </SquareIconButton>

                    {/* <SquareIconButton color={color} ariaLabel="Pin location" hoverText="Pin location" onClick={() => showProgramPoint(program.item)}>
                        <svg className="h-9 w-9" viewBox="0 0 57 62" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.5296 22.8491C10.24 22.4126 9.79109 22.1072 9.27917 21.9987L1.58566 20.3682C-0.0235932 20.0272 -0.554767 17.9931 0.681473 16.9057L19.3321 0.500696C20.5786 -0.595683 22.5384 0.218288 22.6445 1.87639L23.1239 9.37491C23.1591 9.92464 23.4188 10.4355 23.8419 10.7872L36.937 21.671C37.1892 21.8806 37.4891 22.0249 37.8102 22.091L51.7832 24.9689C53.3995 25.3018 53.9387 27.3429 52.6985 28.4338L41.56 38.2312L56.7091 60.1528C57.3848 61.1305 56.0976 62.2627 55.2173 61.4649L35.4801 43.579L24.4169 53.3101C23.1989 54.3814 21.2817 53.6312 21.1116 52.0166L19.5075 36.7969C19.4737 36.476 19.3629 36.1682 19.1847 35.8995L10.5296 22.8491Z" fill="currentColor"/>
                        </svg>
                    </SquareIconButton> */}

                    <SquareIconButton color={color} ariaLabel="Program impacts" hoverText="Show details" onClick={toggleImpact}>
                        <svg fill="#000000" viewBox="-350 -300 2620 2620" xmlns="http://www.w3.org/2000/svg">
                            <path d="M940.253 101.053c158.147 0 314.173 42.644 451.301 123.284l-82.156 139.351C1197.23 297.6 1069.5 262.737 940.253 262.737c-401.179 0-727.579 326.4-727.579 727.579s326.4 727.579 727.58 727.579c401.178 0 727.578-326.4 727.578-727.58 0-129.246-34.964-256.976-101.053-369.246l139.352-82.054c80.741 137.128 123.385 293.254 123.385 451.3 0 490.308-398.956 889.264-889.263 889.264-490.408 0-889.263-398.956-889.263-889.263 0-490.409 398.855-889.263 889.263-889.263Zm.01 303.137v161.685c-234.038 0-424.42 190.383-424.42 424.42 0 234.038 190.382 424.422 424.42 424.422 233.937 0 424.421-190.384 424.421-424.421h161.684c0 323.166-262.939 586.105-586.105 586.105s-586.105-262.94-586.105-586.105c0-323.167 262.939-586.106 586.105-586.106Zm-.02 303.138v161.684c-66.897 0-121.263 54.468-121.263 121.263 0 66.897 54.366 121.264 121.263 121.264s121.263-54.367 121.263-121.264h161.684c0 156.026-126.922 282.948-282.947 282.948S657.296 1146.3 657.296 990.275c0-156.025 126.922-282.947 282.947-282.947Zm736.825-597.05 142.99 142.99-426.341 426.24 186.543 186.543h-515.773V350.28l186.341 186.34 426.24-426.34Z" fill="currentColor"/>
                        </svg>
                    </SquareIconButton>
                </div>

                {/* Video Content */}
                {showVideo && videoUrl && (
                    <div className="mt-6 pl-[5.5rem]">
                    <div className="max-w-4xl overflow-hidden rounded-2xl border bg-black shadow">
                        <div className="relative w-full pb-[56.25%]">
                        <iframe
                            className="absolute inset-0 h-full w-full"
                            src={getEmbedUrl(videoUrl)}
                            title={`${title} video`}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => setShowVideo(false)}
                        className="mt-3 text-sm font-semibold underline"
                        style={{ color }}
                    >
                        Close video
                    </button>
                    </div>
                )}

                {showImages && (
                    <div className="mt-6 pl-[5.5rem]">
                        <ProgramImageGallery images={images} title={title} color={color} />
                        <button
                            type="button"
                            onClick={() => setShowImages(false)}
                            className="mt-3 text-sm font-semibold underline"
                            style={{ color }}
                        >
                            Close gallery
                        </button>
                    </div>
                )}

                {/* Impact content */}
                { showImpact && (
                    <div className="mt-6 pl-[5.5rem]">
                        <div className="max-w-4xl">
                            <ProgramDetailStats program={program}/>
                        </div>

                        <button 
                            type="button"
                            onClick={() => setShowImpact(false)}
                            className="mt-3 text-sm font-semibold underline"
                            style={{ color }}
                        >
                            Close details
                        </button>
                    </div>
                )}

                {/* Pagination */}
                <div className="mt-10 flex items-center gap-2 pl-[86px]">
                    {programs.map((p, index) => (
                        <div key={`${p.title}-${index}`} className="relative group">
                            <button
                                type="button"
                                onClick={() => onSelectProgram(index)}
                                aria-label={`View ${p.title}`}
                                aria-pressed={selectedIndex === index}
                                className="peer cursor-pointer transition-transform duration-150 hover:scale-125"
                            >
                                <Dot active={selectedIndex === index} color={program.color ?? color} />
                            </button>

                            {/* Tooltip */}
                            <div
                                role="tooltip"
                                className="
                                pointer-events-none
                                absolute left-1/2 bottom-full mb-2
                                -translate-x-1/2 translate-y-1
                                opacity-0
                                transition-all duration-150
                                group-hover:opacity-100
                                group-hover:translate-y-0
                                peer-focus-visible:opacity-100
                                peer-focus-visible:translate-y-0
                                "
                            >
                                <div className="relative">
                                {/* Tooltip box */}
                                <div className="rounded-md bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-md whitespace-nowrap">
                                    {p.title}
                                </div>

                                {/* Arrow */}
                                <div
                                    className="
                                    absolute left-1/2 top-[80%]
                                    h-2 w-2
                                    -translate-x-1/2
                                    rotate-45
                                    bg-gray-900
                                    "
                                />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

type SquareIconButtonProps = {
    color: string;
    children: ReactNode;
    ariaLabel: string;
    hoverText: string;
    url?: string;
    onClick?: () => void;
    disabled?: boolean;
};

function SquareIconButton({ color, children, ariaLabel, hoverText, url, onClick, disabled = false }: SquareIconButtonProps) {

    const handleClick = () => {
        if (disabled) return;

        if (onClick) {
            onClick();
            return;
        }

        if (url) {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

  return (
    <div className="flex flex-col items-center group">
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={handleClick}
        className="h-16 w-16 rounded-2xl grid place-items-center shadow-sm
                   transition-all duration-200 hover:scale-[1.03]
                   focus:outline-none focus-visible:ring-4"
        style={{
          backgroundColor: color,
          color: "white",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
        onMouseEnter={(e) => {
            if (disabled) return;
          e.currentTarget.style.backgroundColor = "#e5e7eb";
          e.currentTarget.style.color = color;
        }}
        onMouseLeave={(e) => {
            if (disabled) return;
          e.currentTarget.style.backgroundColor = color;
          e.currentTarget.style.color = "white";
        }}
      >
        {children}
      </button>

      <span className="mt-2 text-xs font-semibold text-port-text opacity-0 translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0">
        {hoverText}
      </span>
    </div>
  );
}

